// components/Navbar.jsx — Fixed top navigation bar with mobile menu

import { useState, useEffect } from "react";
import { Button } from "./UI";

export default function Navbar({ content, dark, setDark, lang, setLang, t, isAdmin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Detect scroll for background blur intensity
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const logo = content?.logo;

  const navItems = [
    { key: "services", label: t.nav.services },
    { key: "portfolio", label: t.nav.portfolio },
    { key: "howItWorks", label: t.nav.howItWorks },
    { key: "pricing", label: t.nav.pricing },
    { key: "testimonials", label: t.nav.testimonials },
    { key: "contact", label: t.nav.contact },
  ];

  return (
    <nav
      className={[
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? dark
            ? "bg-[#0F172A]/95 backdrop-blur-xl border-b border-white/10 shadow-xl shadow-black/20"
            : "bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] shadow-md"
          : "bg-transparent",
        dark ? "text-white" : "text-[#0F172A]",
      ].join(" ")}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center shrink-0" style={{ gap: 12 }}>
          {(logo?.imageUrl || logo?.imageDarkUrl) ? (
            <>
              <img
                src={dark ? (logo?.imageDarkUrl || logo?.imageUrl) : (logo?.imageUrl || logo?.imageDarkUrl)}
                alt={logo.text}
                className="h-8 w-auto object-contain"
              />
              {logo?.text && (
                <span className="text-xl font-bold tracking-tight">
                  <span className="text-[#A3E635]">{logo.text.slice(0, 3)}</span>
                  {logo.text.slice(3)}
                </span>
              )}
            </>
          ) : (
            <span className="text-xl font-bold tracking-tight">
              <span className="text-[#A3E635]">{logo?.text?.slice(0, 3) || "Web"}</span>
              {logo?.text?.slice(3) || "Studio"}
            </span>
          )}
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-6">
          {navItems.map(({ key, label }) => (
            <a
              key={key}
              href={`#${key}`}
              className={`text-sm font-medium transition-colors duration-150 hover:text-[#A3E635] ${
                dark ? "text-white/70" : "text-[#0F172A]/70"
              }`}
            >
              {label}
            </a>
          ))}
          <a
            href="https://blog.webilo.es"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-medium transition-colors duration-150 hover:text-[#A3E635] ${
              dark ? "text-white/70" : "text-[#0F172A]/70"
            }`}
          >
            Blog
          </a>
          {isAdmin && (
            <a
              href="/admin"
              className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#A3E635]/10 text-[#A3E635] border border-[#A3E635]/20 hover:bg-[#A3E635]/20 transition-colors"
            >
              {t.admin}
            </a>
          )}
        </div>

        {/* Controls */}
        <div className="hidden lg:flex items-center gap-3">
          {/* Lang toggle — دو دکمه جداگانه، active بودن بر اساس lang فعلی */}
          <div className="flex gap-1">
            <button
              onClick={() => setLang("en")}
              className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 transition-all ${
                lang === "en"
                  ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/5"
                  : dark
                  ? "border-white/20 text-white/40 hover:border-[#A3E635] hover:text-[#A3E635]"
                  : "border-[#E2E8F0] text-[#0F172A]/40 hover:border-[#A3E635] hover:text-[#A3E635]"
              }`}
            >
              🇬🇧 EN
            </button>
            <button
              onClick={() => setLang("es")}
              className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 transition-all ${
                lang === "es"
                  ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/5"
                  : dark
                  ? "border-white/20 text-white/40 hover:border-[#A3E635] hover:text-[#A3E635]"
                  : "border-[#E2E8F0] text-[#0F172A]/40 hover:border-[#A3E635] hover:text-[#A3E635]"
              }`}
            >
              🇪🇸 ES
            </button>
          </div>

          {/* Dark mode */}
          <button
            onClick={() => setDark(!dark)}
            title={dark ? t.lightMode : t.darkMode}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all text-base hover:bg-[#A3E635]/10 ${
              dark ? "text-white/70 hover:text-[#A3E635]" : "text-[#0F172A]/70 hover:text-[#A3E635]"
            }`}
          >
            {dark ? "☀️" : "🌙"}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2 rounded-lg"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <div className="w-5 space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-0.5 rounded bg-current transition-all duration-300 ${
                  menuOpen
                    ? i === 0
                      ? "rotate-45 translate-y-2"
                      : i === 1
                      ? "opacity-0"
                      : "-rotate-45 -translate-y-2"
                    : ""
                }`}
              />
            ))}
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } ${dark ? "bg-[#0F172A] border-t border-white/10" : "bg-white border-t border-[#E2E8F0]"}`}
      >
        <div className="px-4 py-4 flex flex-col gap-3">
          {navItems.map(({ key, label }) => (
            <a
              key={key}
              href={`#${key}`}
              onClick={() => setMenuOpen(false)}
              className={`text-sm font-medium py-1.5 border-b transition-colors hover:text-[#A3E635] ${
                dark ? "text-white/70 border-white/5" : "text-[#0F172A]/70 border-[#E2E8F0]"
              }`}
            >
              {label}
            </a>
          ))}
          <a
            href="https://blog.webilo.es"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            className={`text-sm font-medium py-1.5 border-b transition-colors hover:text-[#A3E635] ${
              dark ? "text-white/70 border-white/5" : "text-[#0F172A]/70 border-[#E2E8F0]"
            }`}
          >
            Blog
          </a>
          {isAdmin && (
            <a
              href="/admin"
              className="text-sm text-[#A3E635] font-semibold py-1.5"
              onClick={() => setMenuOpen(false)}
            >
              {t.admin} ›
            </a>
          )}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex gap-1">
              <button
                onClick={() => setLang("en")}
                className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 transition-all ${
                  lang === "en"
                    ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/5"
                    : dark
                    ? "border-white/20 text-white/40"
                    : "border-[#E2E8F0] text-[#0F172A]/40"
                }`}
              >
                🇬🇧 EN
              </button>
              <button
                onClick={() => setLang("es")}
                className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 transition-all ${
                  lang === "es"
                    ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/5"
                    : dark
                    ? "border-white/20 text-white/40"
                    : "border-[#E2E8F0] text-[#0F172A]/40"
                }`}
              >
                🇪🇸 ES
              </button>
            </div>
            <button
              onClick={() => setDark(!dark)}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-base"
            >
              {dark ? "☀️" : "🌙"}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
